import { QuickOrderInfo, QuickOrderListItem, QuickOrderProduct } from '../types';
import { api } from './api';
import { ShippingInfo } from '../containers/orders/components/ShippingInfoForm';
import { ShippingMethod } from './companies';
import { PreviewOrder } from './orders';

export type CombinedOrderPreviewPayload = {
	orders: {
		quickOrderId: number;
		items: Pick<QuickOrderProduct, 'id' | 'quantity'>[];
	}[];
	shippingMethodId?: number | null;
	countryCode: string;
};

export type OrderPreviewPayload = {
	quickOrderId: number;
	items: Pick<QuickOrderProduct, 'id' | 'quantity'>[];
	shippingMethodId?: number | null;
	countryCode: string;
};

export type OrderSubmitPayload = {
	quickOrderId: number;
	items: Pick<QuickOrderProduct, 'id' | 'quantity'>[];
	shippingMethodId: number;
	countryCode: string;
	shippingInfo: ShippingInfo;
};

export type CombinedOrderSubmitPayload = {
	orders: {
		quickOrderId: number;
		items: Pick<QuickOrderProduct, 'id' | 'quantity'>[];
	}[];
	shippingMethodId: number;
	countryCode: string;
	shippingInfo: ShippingInfo;
};

export const quickOrdersApi = api.injectEndpoints({
	endpoints: (builder) => ({
		quickOrdersList: builder.query<QuickOrderListItem[], void>({
			query: () => ({
				url: '/quickOrders/list',
				method: 'GET',
			}),
			providesTags: ['QuickOrdersList'],
		}),
		quickOrdersListByPlanIdPublic: builder.query<QuickOrderInfo[], {planId: string, countryCode: string}>({
			query: ({planId, countryCode}) => ({
				url: `/quickOrders/listByPlanIdPublic/${planId}?countryCode=${countryCode}`,
				method: 'GET',
			}),
			providesTags: ['PlanQuickOrdersList'],
		}),
		quickOrderCreate: builder.mutation({
			query: (data: {planId: number}) => ({
				url: '/quickOrders/create',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['QuickOrdersList', 'PlanQuickOrdersList'],
		}),
		quickOrderRemove: builder.mutation({
			query: (orderId: string) => ({
				url: `/quickOrders/${orderId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['QuickOrdersList', 'PlanQuickOrdersList'],
		}),
		quickOrderInfo: builder.query<QuickOrderInfo, string>({
			query: (id: string) => ({
				url: `/quickOrders/${id}/info`,
				method: 'GET',
			}),
			providesTags: ['QuickOrderInfo'],
		}),
		quickOrderFetchInfo: builder.query({
			query: (id: string) => ({
				url: `/quickOrders/${id}/info`,
				method: 'GET',
			}),
		}),
		quickOrderSubmitOffer: builder.mutation({
			query: ({ orderId, data }: { orderId: string, data: any }) => ({
				url: `/quickOrders/${orderId}/submit-offer`,
				method: 'POST',
				body: data,
			}),
		}),
		quickOrderCancelOffer: builder.mutation({
			query: ({ orderId, data }: { orderId: string, data: any }) => ({
				url: `/quickOrders/${orderId}/cancel-offer`,
				method: 'POST',
				body: data,
			}),
		}),
		quickOrderSubmit: builder.mutation<{paymentIntent: {id: string, clientSecret: string}, pendingOrder: {id: number, products: {id: number, quantity: string}[]}}, OrderSubmitPayload>({
			query: (data: OrderSubmitPayload) => ({
				url: 'quickOrders/submitOrder',
				body: data,
				method: 'POST',
			}),
		}),
		quickOrderSubmitCombinedOrder: builder.mutation<{paymentIntent: {id: string, clientSecret: string}, pendingOrder: {id: number, products: {id: number, quantity: string}[]}}, CombinedOrderSubmitPayload>({
			query: (data: CombinedOrderSubmitPayload) => ({
				url: 'quickOrders/submitCombinedOrder',
				body: data,
				method: 'POST',
			}),
		}),
		quickOrderPreview: builder.mutation<{totalInCents: number, previewOrder: PreviewOrder, combinedOrder: PreviewOrder, shippingMethods: ShippingMethod[], country: string}, OrderPreviewPayload>({
			query: (data: OrderPreviewPayload) => ({
				url: '/quickOrders/previewOrder',
				method: 'POST',
				body: data,
			}),
		}),
		quickOrderPreviewCombinedOrder: builder.mutation<{combinedOrders: PreviewOrder[], shippingMethods: ShippingMethod[], country: string}, CombinedOrderPreviewPayload>({
			query: (data: CombinedOrderPreviewPayload) => ({
				url: '/quickOrders/previewCombinedOrder',
				method: 'POST',
				body: data,
			}),
		}),
		quickOrderUpdate: builder.mutation<Partial<QuickOrderInfo>, Partial<Omit<QuickOrderInfo, 'id'>> & {id: string}>({
			query: ({ id, ...props }) => ({
				url: `/quickOrders/${id}`,
				method: 'PUT',
				body: props,
			}),
			invalidatesTags: ['QuickOrdersList', 'PlanQuickOrdersList'],
		}),
		quickOrderPublish: builder.mutation<{ publishedAt: string }, string>({
			query: (id) => ({
				url: `/quickOrders/${id}/publish`,
				method: 'PUT',
			}),
			invalidatesTags: ['QuickOrdersList', 'PlanQuickOrdersList', 'QuickOrderInfo'],
		}),
		quickOrderUnpublish: builder.mutation<{ publishedAt: null }, string>({
			query: (id) => ({
				url: `/quickOrders/${id}/unpublish`,
				method: 'PUT',
			}),
			invalidatesTags: ['QuickOrdersList', 'PlanQuickOrdersList', 'QuickOrderInfo'],
		}),
	}),
});

export const {
	useQuickOrderCreateMutation,
	useQuickOrderFetchInfoQuery,
	useQuickOrderSubmitOfferMutation,
	useQuickOrderCancelOfferMutation,
	useQuickOrderRemoveMutation,
	useQuickOrderSubmitMutation,
	useQuickOrderSubmitCombinedOrderMutation,
	useQuickOrderPreviewMutation,
	useQuickOrderPreviewCombinedOrderMutation,
	useQuickOrderPublishMutation,
	useQuickOrderUnpublishMutation,
	useQuickOrdersListQuery,
	useQuickOrdersListByPlanIdPublicQuery,
	useQuickOrderUpdateMutation,
	useQuickOrderInfoQuery,
} = quickOrdersApi;
